<!-- src/views/LandingForPlannersNoFlight.vue -->
<template>
  <div class="internal-bady landing-planners" :dir="isHebrew ? 'rtl' : 'ltr'" :lang="isHebrew ? 'he' : 'en'" :aria-label="t('landingPlannersMetaTitle')">
    <NavBar />
    <div id="top"></div>

    <div class="px-3 px-md-4" id="welcome-section">
      <div :style="heroStyle" class="container-fluid bg-welcome rounded-5 d-flex align-items-center">
        <div class="container position-index">
          <div class="row justify-content-center">
            <div class="col-12 col-lg-10 col-xl-8 d-flex flex-column gap-3 gap-md-4 text-center hero-copy">
              <h1 class="text-welcome text-white mb-0">{{ t('landingPlannersNoFlightHero') }}</h1>
              <p class="fs-4 fw-semibold text-white mb-0 hero-subtitle">
                {{ t('landingPlannersNoFlightHeroSub1Line1') }}<br />
                {{ t('landingPlannersNoFlightHeroSub1Line2') }}
              </p>
              <p class="fs-4 fw-semibold text-white mb-0 hero-subtitle">
                {{ t('landingPlannersNoFlightHeroSub2Line1') }}<br />
                {{ t('landingPlannersNoFlightHeroSub2Line2') }}
              </p>
              <p class="fs-4 fw-semibold text-white mb-0 hero-subtitle">
                {{ t('landingPlannersNoFlightHeroSub3Line1') }}<br />
                {{ t('landingPlannersNoFlightHeroSub3Line2') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section class="py-4 py-md-5" aria-labelledby="intro-title">
      <div class="container intro-container">
        <div class="d-flex justify-content-center">
          <div class="w-75 full-width">
            <h2 id="intro-title" class="text-center fw-bold black margin-for-h2">
              {{ t('landingPlannersWhatIsTitlePrefix') }} <span class="text-primary">Trip</span><span class="text-secondary">Planner</span>{{ t('landingPlannersWhatIsTitleSuffix') }}
            </h2>
            <p class="text-muted text-center fs-6 what_can_be_done_explanation">
              {{ t('landingPlannersNoFlightWhatIsBody1Line1') }}<br />
              {{ t('landingPlannersNoFlightWhatIsBody1Line2') }}<br />
              {{ t('landingPlannersNoFlightWhatIsBody1Line3') }}
            </p>
            <p class="text-muted text-center fs-6 what_can_be_done_explanation mb-0">
              {{ t('landingPlannersNoFlightWhatIsBody2Line1') }}<br />
              {{ t('landingPlannersNoFlightWhatIsBody2Line2') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="container-fluid py-5 bg-light-blue" aria-labelledby="showcase-title">
      <div class="container">
        <h2 id="showcase-title" class="text-center fw-bold black">{{ t('landingPlannersHowItLooks') }}</h2>
        <p class="text-muted text-center fs-6 what_can_be_done_explanation">
          {{ t('landingPlannersHowItLooksSub') }}
        </p>

        <div class="d-flex justify-content-center">
          <div class="card border-0 rounded-5 shadow-sm p-2 p-md-3 showcase-card w-100">
            <div class="card-body">
              <div class="carousel" dir="ltr">
                <div class="carousel__container">
                  <div class="carousel__track" :style="{ transform: `translateX(${currentSlide * 100}%)` }">
                    <div v-for="(screenshot, index) in screenshots" :key="index" class="carousel__slide">
                      <div class="phone-mockup">
                        <img
                          :src="screenshot.url"
                          :alt="screenshot.caption"
                          class="phone-mockup__screen"
                          loading="lazy"
                        />
                      </div>
                      <p class="carousel__caption" dir="rtl">{{ screenshot.caption }}</p>
                    </div>
                  </div>
                </div>

                <button
                  class="carousel__btn carousel__btn--prev"
                  @click="prevSlide"
                  :disabled="currentSlide === 0"
                  :aria-label="t('landingPlannersPreviousImage')"
                  type="button"
                >
                  ❮
                </button>
                <button
                  class="carousel__btn carousel__btn--next"
                  @click="nextSlide"
                  :disabled="currentSlide === -(screenshots.length - 1)"
                  :aria-label="t('landingPlannersNextImage')"
                  type="button"
                >
                  ❯
                </button>

                <div class="carousel__dots">
                  <button
                    v-for="(screenshot, index) in screenshots"
                    :key="`dot-${index}`"
                    class="carousel__dot"
                    :class="{ 'carousel__dot--active': currentSlide === -index }"
                    @click="goToSlide(index)"
                    :aria-label="t('landingPlannersGoToImage', { index: index + 1 })"
                    type="button"
                  ></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="container-fluid position-relative overflow-hidden py-5 benefits-section" aria-labelledby="campaign-why-now-title benefits-title">
      <div>
        <img
          alt=""
          class="background-image background-image-max-with-none"
          src="https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fcircles-background.png?alt=media&token=d420088e-66c6-4c95-bd3b-2f218dd36aba"
        />
      </div>
      <div class="container py-3 py-md-5">
        <div class="d-flex justify-content-center">
          <div class="w-75 full-width">
            <h2 id="campaign-why-now-title" class="text-center fw-bold black margin-for-h2">
              {{ t('landingPlannersNoFlightCampaignWhyNowTitle') }}
            </h2>
            <h2 id="benefits-title" class="text-center fw-bold black margin-for-h2 mt-4 pt-2">
              {{ t('landingPlannersBenefitsTitle') }}
            </h2>
            <p class="text-muted text-center fs-6 what_can_be_done_explanation mb-0">
              {{ t('landingPlannersBenefitsIntro') }}
            </p>
          </div>
        </div>

        <div class="row justify-content-center align-items-stretch mt-4">
          <div v-for="(benefit, idx) in benefits" :key="idx" class="col-md-6 col-lg-4 mb-3 d-flex">
            <div class="card border-0 rounded-5 shadow-sm p-md-2 w-100 d-flex flex-column">
              <div class="card-body d-flex flex-column gap-md-3 gap-2 text-center flex-grow-1">
                <div class="benefit-icon rounded-circle mx-auto d-flex align-items-center justify-content-center">
                  <i class="fa-solid fa-check" aria-hidden="true"></i>
                </div>
                <h4 class="fw-bold mb-0 black">{{ benefit.title }}</h4>
                <p class="fs-6 mb-0 text-muted what_can_be_done_explanation">{{ benefit.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center pt-3">
          <button class="btn btn-primary px-5 py-3 rounded-pill fw-semibold fs-5" type="button" @click="openContactForm">
            {{ t('landingPlannersLeaveDetails') }}
          </button>
        </div>
      </div>
    </section>

    <section class="container py-4 py-md-5" aria-labelledby="demo-title">
      <div class="d-flex justify-content-center">
        <div class="w-75 full-width">
          <h2 id="demo-title" class="text-center fw-bold black">{{ t('landingPlannersSampleItinerary') }}</h2>
          <p class="text-muted text-center fs-6 what_can_be_done_explanation">
            {{ t('landingPlannersDemoExplainer') }}
          </p>

          <div class="card border-0 rounded-5 shadow-sm p-2 p-md-3">
            <div class="card-body text-center">
              <a :href="demoLink" target="_blank" rel="noopener" class="demo-link">
                <span class="demo-link__label">{{ t('landingPlannersOpenSample') }}</span>
                <span class="demo-link__url">{{ demoLink }}</span>
              </a>
              <p class="text-muted fs-6 what_can_be_done_explanation mb-0 mt-3">
                {{ t('landingPlannersDemoFooter') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="container py-4 py-md-5" id="contact-form" aria-labelledby="contact-form-title">
      <div class="d-flex justify-content-center">
        <div class="w-75 full-width">
          <div class="card border-0 rounded-5 shadow-sm p-2 p-md-3">
            <div class="card-body">
              <p class="text-center fw-bold black mb-2 contact-pretitle">
                {{ t('landingPlannersInterestedQuestion') }}
              </p>
              <p class="text-muted text-center fs-6 what_can_be_done_explanation mb-4 contact-pretitle-sub">
                {{ t('landingPlannersInterestedSub') }}
              </p>
              <h2 id="contact-form-title" class="text-center fw-bold black">{{ t('landingPlannersContactUsTitle') }}</h2>
              <p class="text-muted text-center fs-6 what_can_be_done_explanation">
                {{ t('landingPlannersContactUsSubtitle') }}
              </p>

              <form @submit.prevent="handleContactSubmit" class="contact-form">
                <div class="form-group mb-3">
                  <b-form-input
                    v-model="contactFormData.name"
                    :placeholder="t('landingPlannersFormFullName')"
                    class="contact-input"
                    size="lg"
                    required
                  />
                </div>
                <div class="form-group mb-3">
                  <b-form-input
                    v-model="contactFormData.phone"
                    type="tel"
                    :placeholder="t('landingPlannersFormPhone')"
                    class="contact-input"
                    size="lg"
                    required
                    autocomplete="tel"
                  />
                </div>
                <div class="form-group mb-3">
                  <b-form-input
                    v-model="contactFormData.email"
                    type="email"
                    :placeholder="t('landingPlannersFormEmail')"
                    class="contact-input"
                    size="lg"
                  />
                </div>
                <div class="form-group mb-3">
                  <b-form-textarea
                    v-model="contactFormData.message"
                    :placeholder="t('landingPlannersFormMessage')"
                    class="contact-textarea"
                    rows="4"
                  />
                </div>
                <div class="form-group mb-3">
                  <label class="d-flex align-items-start gap-2 mb-0">
                    <input
                      type="checkbox"
                      v-model="contactFormData.privacyPolicyAccepted"
                      class="mt-1"
                      required
                    />
                    <span>
                      {{ t('landingPlannersFormAcceptPrefix') }}
                      <router-link to="/privacy_policy" target="_blank" class="privacy-link" @click.stop>{{ t('landingPlannersFormPrivacyPolicyLink') }}</router-link>
                    </span>
                  </label>
                </div>

                <div class="text-center mt-4">
                  <b-button type="submit" class="btn btn-primary px-5 py-3 rounded-pill fw-semibold fs-5" :disabled="contactFormLoading">
                    <span v-if="contactFormLoading" class="spinner-border spinner-border-sm mr-2"></span>
                    {{ contactFormLoading ? t('landingPlannersFormSending') : t('landingPlannersFormSubmit') }}
                  </b-button>
                </div>

                <div v-if="contactFormMessage" class="mt-3 text-center" :class="contactFormMessageType === 'success' ? 'text-success' : 'text-danger'">
                  {{ contactFormMessage }}
                </div>
              </form>

              <div class="contact-alternative" :aria-label="t('landingPlannersAlternativeContact')">
                <div class="contact-alternative__divider" aria-hidden="true"></div>
                <p class="text-muted text-center fs-6 what_can_be_done_explanation mb-3">
                  {{ t('landingPlannersAlsoSendWhatsApp') }}
                </p>
                <div class="text-center">
                  <button
                    type="button"
                    class="btn btn-primary px-5 py-3 rounded-pill fw-semibold fs-5"
                    :aria-label="t('landingPlannersOpenWhatsApp')"
                    @click="openWhatsappChat"
                  >
                    {{ t('landingPlannersSendWhatsApp') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="container py-4 py-md-5 closing-container" aria-labelledby="closing-title">
      <div class="d-flex justify-content-center">
        <div class="w-75 full-width text-center">
          <h2 id="closing-title" class="fw-bold black">{{ t('landingPlannersToSummary') }}</h2>
          <p class="text-muted fs-6 what_can_be_done_explanation">
            {{ t('landingPlannersClosingBody1') }}
          </p>
          <p class="text-muted fs-6 what_can_be_done_explanation mb-0">
            {{ t('landingPlannersClosingBody2') }}
          </p>
        </div>
      </div>
    </section>

    <PageFooterNew />
  </div>
</template>

<script>
import contactApi from '@/api/contact';
import { isValidContactPhone } from '@/common/validateContactPhone';
import NavBar from '@/components/NavBar.vue'
import PageFooterNew from '@/components/PageFooterNew.vue'
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: 'LandingForPlannersNoFlight',
  mixins: [translationMixin],
  components: {
    NavBar,
    PageFooterNew,
  },
  data() {
    return {
      heroStyle: {
        '--home-bg-image': `url('https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fwelcome-trip.jpg?alt=media&token=0454fe77-65ec-4ece-8c99-6d978d2a7bbd')`
      },
      previousHtmlFontSize: '',
      contactFormData: {
        name: '',
        email: '',
        phone: '',
        message: '',
        source: 'landing_page_planners_no_flight',
        privacyPolicyAccepted: false
      },
      contactFormLoading: false,
      contactFormMessage: '',
      contactFormMessageType: '',
      currentSlide: 0,
      screenshotUrls: [
        'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_ride_sumery.png?alt=media&token=6ac83bff-0d6d-4f87-a12d-b79fcaf20bf2',
        'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_day_at_the_ride.png?alt=media&token=6a746668-6178-404b-9c45-1dc4b982e38e',
        'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_ride_atraction_summery.png?alt=media&token=cbfde260-cda4-4471-8266-b524739239f5',
        'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_prisenting%20_the_map.png?alt=media&token=4cb13652-f90c-4c2a-b067-a8b69066aae7',
        'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/for-planners%2FScreenshot_spain_nevigate_to_destination.png?alt=media&token=f58fb2cd-3266-47c0-8a57-f3a851614762'
      ]
    }
  },
  props: {
    demoLink: {
      type: String,
      default: 'https://tripplanner.co.il/single_ride_with_map/6095'
    },
    /* Priority: calendarUrl > whatsappNumber > tel > email */
    calendarUrl: { type: String, default: '' }, // e.g. Calendly / Google Calendar appointment scheduling
    whatsappNumber: { type: String, default: '972526867915' }, // international format without +
    whatsappMessage: {
      type: String,
      default: ''
    },
    tel: { type: String, default: '' },   // example: 050-1234567
    email: { type: String, default: '' }  // example: hello@tripplanner.co.il
  },
  computed: {
    benefits() {
      return [
        { title: this.t('landingPlannersProtectYourContent'), description: this.t('landingPlannersProtectYourContentBody') },
        { title: this.t('landingPlannersProfessionalLook'), description: this.t('landingPlannersProfessionalLookBody') },
        { title: this.t('landingPlannersAllInOnePlace'), description: this.t('landingPlannersAllInOnePlaceBody') },
        { title: this.t('landingPlannersEasyClientAccess'), description: this.t('landingPlannersEasyClientAccessBody') },
        { title: this.t('landingPlannersRealTimeSaving'), description: this.t('landingPlannersRealTimeSavingBody') },
        { title: this.t('landingPlannersOfflineConvenience'), description: this.t('landingPlannersOfflineConvenienceBody') }
      ];
    },
    screenshots() {
      const captions = [
        this.t('landingPlannersFullViewAlt'),
        this.t('landingPlannersDailyDetailAlt'),
        this.t('landingPlannersAllActivitiesAlt'),
        this.t('landingPlannersDayMapAlt'),
        this.t('landingPlannersDirectNavAlt')
      ];
      return this.screenshotUrls.map((url, i) => ({ url, caption: captions[i] }));
    }
  },
  methods: {
    prevSlide() {
      if (this.currentSlide < 0) {
        this.currentSlide++;
      }
    },
    nextSlide() {
      if (this.currentSlide > -(this.screenshots.length - 1)) {
        this.currentSlide--;
      }
    },
    goToSlide(index) {
      this.currentSlide = -index;
    },
    openContactForm() {
      // Scroll to the contact form section
      const formElement = document.getElementById('contact-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    openWhatsappChat() {
      if (!this.whatsappNumber) {
        return;
      }
      const message = this.whatsappMessage || this.t('landingPlannersNoFlightWhatsAppMessage');
      const msg = encodeURIComponent(message || '');
      const url = msg
        ? `https://wa.me/${this.whatsappNumber}?text=${msg}`
        : `https://wa.me/${this.whatsappNumber}`;
      window.open(url, '_blank', 'noopener');
    },
    async handleContactSubmit() {
      if (!this.validateContactForm()) {
        return;
      }
      
      this.contactFormLoading = true;
      this.contactFormMessage = '';
      
      try {
        const success = await contactApi.submitContactForm(this.contactFormData);
        
        if (success) {
          this.contactFormMessage = this.t('landingPlannersMessageSentSuccess');
          this.contactFormMessageType = 'success';
          this.resetContactForm();
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        this.contactFormMessage = this.t('landingPlannersMessageSentError');
        this.contactFormMessageType = 'error';
      } finally {
        this.contactFormLoading = false;
      }
    },
    validateContactForm() {
      if (!this.contactFormData.name.trim()) {
        this.contactFormMessage = this.t('landingPlannersEnterName');
        this.contactFormMessageType = 'error';
        return false;
      }

      if (!this.contactFormData.phone.trim()) {
        this.contactFormMessage = this.t('landingPlannersEnterPhone');
        this.contactFormMessageType = 'error';
        return false;
      }
      if (!isValidContactPhone(this.contactFormData.phone)) {
        this.contactFormMessage = this.t('landingPlannersEnterValidPhone');
        this.contactFormMessageType = 'error';
        return false;
      }

      // Validate email only if provided
      if (this.contactFormData.email.trim() && !this.validateEmail(this.contactFormData.email)) {
        return false;
      }

      // Validate privacy policy acceptance
      if (!this.contactFormData.privacyPolicyAccepted) {
        this.contactFormMessage = this.t('landingPlannersConfirmPrivacy');
        this.contactFormMessageType = 'error';
        return false;
      }

      return true;
    },
    validateEmail(email) {
      const trimmedEmail = email.trim();

      if (trimmedEmail.includes(' ')) {
        this.contactFormMessage = this.t('landingPlannersEmailNoSpaces');
        this.contactFormMessageType = 'error';
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        this.contactFormMessage = this.t('landingPlannersEnterValidEmail');
        this.contactFormMessageType = 'error';
        return false;
      }

      return true;
    },
    resetContactForm() {
      this.contactFormData = {
        name: '',
        email: '',
        phone: '',
        message: '',
        source: 'landing_page_planners_no_flight',
        privacyPolicyAccepted: false
      };
    },
  }
  ,
  mounted() {
    try {
      this.previousHtmlFontSize = document.documentElement.style.fontSize || ''
      document.documentElement.style.fontSize = '100%'
    } catch (e) {
      // ignore
    }
  },
  beforeDestroy() {
    try {
      document.documentElement.style.fontSize = this.previousHtmlFontSize || ''
    } catch (e) {
      // ignore
    }
  }
}
</script>

<style scoped>
.internal-bady {
  font-family: "Noto Sans Hebrew", "Rubik", "Nunito", sans-serif;
  color: #1e1e1e;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.landing-planners {
  font-size: 18px;
  font-weight: 500;
}

.landing-planners .text-muted {
  color: rgba(33, 37, 41, 0.82) !important;
}

.landing-planners p,
.landing-planners li {
  line-height: 1.75;
}

.landing-planners .fw-semibold {
  font-weight: 700 !important;
}

.landing-planners .fw-bold {
  font-weight: 800 !important;
}

.landing-planners h2,
.landing-planners .h2 {
  font-size: calc(1.9rem + 0.6vw);
}

.landing-planners h4,
.landing-planners .h4 {
  font-size: 1.65rem;
}

.landing-planners .fs-6 {
  font-size: 1.25rem !important;
}

#welcome-section {
  padding-top: 98px;
  padding-bottom: 24px;
  height: 100dvh;
}

.text-welcome {
  font-size: 72px;
  font-weight: 800;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.45);
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

#welcome-section .fs-4 {
  font-size: 1.75rem !important;
}

.bg-welcome {
  position: relative;
  overflow: hidden;
  height: 100%;
}

.bg-welcome::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--home-bg-image);
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 32px;
  z-index: 0;
}

.bg-welcome::after {
  position: absolute;
  content: '';
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 32px;
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)),
    linear-gradient(45deg, rgba(43, 43, 43, 0), rgba(30, 30, 30, 0.8));
  z-index: 1;
}

.position-index {
  position: relative;
  z-index: 2;
}

.rounded-5 {
  border-radius: 32px !important;
}

.text-primary {
  background-image: linear-gradient(45deg, #34D0B6, #07A0C3) !important;
  color: transparent !important;
  -webkit-background-clip: text !important;
  -moz-background-clip: text !important;
  background-clip: text !important;
}

.text-secondary {
  color: #0D2F29 !important;
}

.btn-primary {
  background: linear-gradient(45deg, #34D0B6, #07A0C3);
  color: white;
  border: 0;
}

.btn-primary:hover {
  background: linear-gradient(45deg, #39e1c5, #08add3);
  color: white;
  border: 0;
}

.btn-warning {
  background: linear-gradient(45deg, #E0BE36, #DD6E42);
  color: white;
  border: 0;
}

.btn-warning:hover {
  background: linear-gradient(45deg, #e8c538, #ee7748);
  color: white;
  border: 0;
}

.bg-light-blue {
  background-color: #E6F5F9 !important;
}

.background-image {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  z-index: -1;
}

.background-image-max-with-none {
  max-width: none;
}

.black {
  color: #1e1e1e;
}

.what_can_be_done_explanation {
  color: rgba(33, 37, 41, 0.82) !important;
  font-size: 1.25rem !important;
  font-weight: 600;
  margin-bottom: 1.6rem;
  max-width: 920px;
  margin-left: auto;
  margin-right: auto;
}

.benefits-section h4,
.benefits-section .h4 {
  font-size: 1.4rem;
}

.benefits-section .what_can_be_done_explanation {
  font-size: 1.1rem !important;
}

@media (min-width: 992px) {
  .landing-planners {
    font-size: 20px;
  }

  .landing-planners h2,
  .landing-planners .h2 {
    font-size: clamp(2.4rem, 2.0rem + 1.0vw, 3.1rem);
  }

  .landing-planners h4,
  .landing-planners .h4 {
    font-size: 1.9rem;
  }

  .landing-planners .fs-6 {
    font-size: 1.4rem !important;
  }

  .what_can_be_done_explanation {
    font-size: 1.4rem !important;
  }

  #welcome-section .fs-4 {
    font-size: 2.0rem !important;
  }

  .benefits-section h4,
  .benefits-section .h4 {
    font-size: 1.55rem;
  }

  .benefits-section .what_can_be_done_explanation {
    font-size: 1.2rem !important;
  }
}

.benefit-icon {
  height: 56px;
  width: 56px;
  background: rgba(7, 160, 195, 0.12);
  color: #07A0C3;
  font-size: 22px;
}

.demo-link {
  display: block;
  padding: 18px 22px;
  border-radius: 18px;
  background: rgba(7, 160, 195, 0.10);
  color: #0b6f85;
  font-weight: 800;
  text-decoration: none;
  word-break: break-all;
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid rgba(7, 160, 195, 0.18);
}

.demo-link:hover {
  text-decoration: underline;
  color: #0b6f85;
}

.demo-link__label {
  display: block;
  font-size: 1.25rem;
  line-height: 1.25;
}

.demo-link__url {
  display: block;
  margin-top: 6px;
  font-size: 0.9rem;
  font-weight: 700;
  opacity: 0.9;
}

.contact-input,
.contact-textarea {
  border: 2px solid rgba(7, 160, 195, 0.2);
  border-radius: 12px;
}

#contact-form .contact-input,
#contact-form .contact-textarea {
  font-size: 1.125rem !important;
  padding: 18px 20px !important;
}

#contact-form .contact-textarea {
  min-height: 160px;
}

#contact-form label,
#contact-form .privacy-link {
  font-size: 1rem;
}

#contact-form input[type="checkbox"] {
  width: 20px;
  height: 20px;
}

#contact-form button[type="submit"] {
  font-size: 1.25rem !important;
  padding: 16px 48px !important;
}

.contact-pretitle {
  font-size: 1.5rem;
}

.contact-pretitle-sub {
  margin-left: auto;
  margin-right: auto;
  max-width: 900px;
}

.contact-alternative {
  margin-top: 28px;
}

.contact-alternative__divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 24px auto;
  max-width: 520px;
}

.privacy-link {
  color: #07A0C3;
  text-decoration: underline;
}

.privacy-link:hover {
  color: #07A0C3;
  text-decoration: underline;
}

.showcase-card {
  max-width: 820px;
}

.carousel {
  position: relative;
  max-width: 520px;
  margin: 0 auto;
  padding: 0 50px;
}

.carousel__container {
  overflow: hidden;
  border-radius: 24px;
  direction: ltr;
  width: 100%;
}

.carousel__track {
  display: flex;
  transition: transform 0.5s ease-in-out;
  direction: ltr;
  width: 100%;
}

.carousel__slide {
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

/* Phone Mockup */
.phone-mockup {
  position: relative;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
  background: #1a1a1a;
  border-radius: 32px;
  padding: 10px;
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.5),
    0 0 0 2px rgba(255, 255, 255, 0.1),
    inset 0 0 0 2px rgba(0, 0, 0, 0.8);
  animation: phoneFloat 3s ease-in-out infinite;
}

.phone-mockup::before {
  content: '';
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 5px;
  background: #333;
  border-radius: 3px;
  z-index: 10;
}

.phone-mockup__screen {
  width: 100%;
  height: auto;
  border-radius: 26px;
  display: block;
  background: #fff;
}

.carousel__caption {
  margin-top: 20px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(33, 37, 41, 0.75);
  line-height: 1.5;
  max-width: 300px;
  padding: 0 10px;
}

/* Navigation Buttons */
.carousel__btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.95);
  color: #07A0C3;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  touch-action: manipulation;
}

.carousel__btn:hover:not(:disabled) {
  background: #fff;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.4);
}

.carousel__btn:active:not(:disabled) {
  transform: translateY(-50%) scale(0.95);
}

.carousel__btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.carousel__btn--prev {
  left: 0;
}

.carousel__btn--next {
  right: 0;
}

/* Dots Indicator */
.carousel__dots {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 32px;
}

.carousel__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

.carousel__dot:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: scale(1.2);
}

.carousel__dot--active {
  background: #fff;
  width: 32px;
  border-radius: 6px;
}

@keyframes phoneFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Responsive Design */
@media (max-width: 767px) {
  #welcome-section {
    padding-top: 74px;
    padding-bottom: 16px;
    height: 100dvh;
    min-height: 100dvh;
  }

  .text-welcome {
    font-size: 34px;
    font-weight: 800;
  }

  #welcome-section .fs-4 {
    font-size: 18px !important;
  }

  .landing-planners {
    font-size: 16px;
  }

  .landing-planners h2,
  .landing-planners .h2 {
    font-size: 28px;
  }

  .landing-planners h4,
  .landing-planners .h4 {
    font-size: 22px;
  }

  .landing-planners .fs-6 {
    font-size: 16px !important;
  }

  .what_can_be_done_explanation {
    font-size: 16px !important;
  }

  #contact-form .contact-input,
  #contact-form .contact-textarea {
    font-size: 16px !important;
    padding: 14px 16px !important;
  }

  #contact-form label,
  #contact-form .privacy-link {
    font-size: 14px;
  }

  #contact-form button[type="submit"] {
    font-size: 18px !important;
    padding: 14px 36px !important;
  }

  .full-width {
    width: 100% !important;
  }

  .intro-container {
    padding-left: 18px !important;
    padding-right: 18px !important;
  }

  #demo-title {
    padding-left: 6px;
    padding-right: 6px;
  }

  #demo-title + p {
    padding-left: 18px;
    padding-right: 18px;
  }

  .closing-container {
    padding-left: 18px !important;
    padding-right: 18px !important;
  }
}

@media (min-width: 768px) {
  .carousel {
    max-width: 500px;
    padding: 0 80px;
  }

  .carousel__btn {
    width: 56px;
    height: 56px;
    font-size: 28px;
  }
  
  .phone-mockup {
    max-width: 350px;
  }

  .carousel__caption {
    font-size: 16px;
  }

}

</style>

